/**
 * SessionManager - Utility for managing user session data
 * Handles storage, retrieval, validation, and clearing of session data
 */

const SESSION_KEYS = {
  AUTH_TOKEN: 'authToken',
  USER_DATA: 'userData',
  LOGIN_TIME: 'loginTime',
};

// Backend does not yet issue/validate a real session token (see CRM_DEVELOPMENT_ROADMAP.md
// Phase 1), so expiry is enforced client-side only as a stopgap.
const SESSION_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

class SessionManager {
  /**
   * Get authentication token from storage
   * @returns {string|null} The authentication token or null if not found
   */
  static getToken() {
    try {
      return localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
    } catch (error) {
      console.error('[SessionManager] Error getting token:', error);
      return null;
    }
  }

  /**
   * Get user data from storage
   * @returns {object|null} The user data object or null if not found/invalid
   */
  static getUserData() {
    try {
      const userDataStr = localStorage.getItem(SESSION_KEYS.USER_DATA);
      if (!userDataStr) return null;

      const userData = JSON.parse(userDataStr);
      
      // Validate user data structure
      if (this.isValidUserData(userData)) {
        return userData;
      } else {
        console.warn('[SessionManager] Invalid user data structure, clearing...');
        this.clearSession();
        return null;
      }
    } catch (error) {
      console.error('[SessionManager] Error parsing user data:', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Validate user data structure
   * @param {object} userData - User data object to validate
   * @returns {boolean} True if valid, false otherwise
   */
  static isValidUserData(userData) {
    if (!userData || typeof userData !== 'object') {
      return false;
    }

    // Check if userData is not empty
    const hasData = Object.keys(userData).length > 0;

    // For this CRM app, we treat any non-empty user object as valid.
    // The backend may not always return standard id/email fields,
    // but AuthContext always stores a normalized user object.
    return hasData;
  }

  /**
   * Check if user has a valid session
   * @returns {boolean} True if both token and valid userData exist
   */
  static hasValidSession() {
    const token = this.getToken();
    const userData = this.getUserData();

    if (!token || !userData) {
      return false;
    }

    if (this.isSessionExpired()) {
      this.clearSession();
      return false;
    }

    return true;
  }

  /**
   * Set authentication token
   * @param {string} token - Authentication token to store
   */
  static setToken(token) {
    try {
      if (token) {
        localStorage.setItem(SESSION_KEYS.AUTH_TOKEN, token);
      } else {
        localStorage.removeItem(SESSION_KEYS.AUTH_TOKEN);
      }
    } catch (error) {
      console.error('[SessionManager] Error setting token:', error);
    }
  }

  /**
   * Set user data
   * @param {object} userData - User data object to store
   */
  static setUserData(userData) {
    try {
      if (userData && this.isValidUserData(userData)) {
        localStorage.setItem(SESSION_KEYS.USER_DATA, JSON.stringify(userData));
      } else {
        console.warn('[SessionManager] Invalid user data, not storing');
        this.clearSession();
      }
    } catch (error) {
      console.error('[SessionManager] Error setting user data:', error);
    }
  }

  /**
   * Set complete session (token + userData)
   * @param {string} token - Authentication token
   * @param {object} userData - User data object
   */
  static setSession(token, userData) {
    this.setToken(token);
    this.setUserData(userData);
    try {
      localStorage.setItem(SESSION_KEYS.LOGIN_TIME, String(Date.now()));
    } catch (error) {
      console.error('[SessionManager] Error setting login time:', error);
    }
  }

  /**
   * Update user data (merge with existing or replace)
   * @param {object} updates - User data updates to apply
   * @param {boolean} merge - If true, merge with existing data; if false, replace
   */
  static updateUserData(updates, merge = true) {
    try {
      if (merge) {
        const existingData = this.getUserData() || {};
        const updatedData = { ...existingData, ...updates };
        this.setUserData(updatedData);
      } else {
        this.setUserData(updates);
      }
    } catch (error) {
      console.error('[SessionManager] Error updating user data:', error);
    }
  }

  /**
   * Clear all session data
   */
  static clearSession() {
    try {
      localStorage.removeItem(SESSION_KEYS.AUTH_TOKEN);
      localStorage.removeItem(SESSION_KEYS.USER_DATA);
      localStorage.removeItem(SESSION_KEYS.LOGIN_TIME);
    } catch (error) {
      console.error('[SessionManager] Error clearing session:', error);
    }
  }

  /**
   * Get complete session data
   * @returns {object} Object containing token and userData, or null if no valid session
   */
  static getSession() {
    const token = this.getToken();
    const userData = this.getUserData();

    if (token && userData) {
      return {
        token,
        userData,
      };
    }

    return null;
  }

  /**
   * Check if session is expired (if you implement expiration logic)
   * @returns {boolean} True if session is expired
   */
  static isSessionExpired() {
    try {
      const loginTimeStr = localStorage.getItem(SESSION_KEYS.LOGIN_TIME);
      if (!loginTimeStr) {
        // Sessions created before this field existed have no login time recorded;
        // treat as expired so they re-authenticate and get one.
        return true;
      }
      const loginTime = parseInt(loginTimeStr, 10);
      return Number.isNaN(loginTime) || Date.now() - loginTime > SESSION_TTL_MS;
    } catch (error) {
      console.error('[SessionManager] Error checking session expiry:', error);
      return true;
    }
  }

  /**
   * Refresh session (extends session lifetime by resetting the login time)
   */
  static refreshSession() {
    if (!this.hasValidSession()) {
      this.clearSession();
      return;
    }
    try {
      localStorage.setItem(SESSION_KEYS.LOGIN_TIME, String(Date.now()));
    } catch (error) {
      console.error('[SessionManager] Error refreshing session:', error);
    }
  }
}

export default SessionManager;
